import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';
import { unwrapApiData } from './unwrapApiData';
import type {
  CreateFaqInput,
  FaqItem,
  FaqListQuery,
  FaqListResponse,
  UpdateFaqInput,
} from '../../application/types';

export const faqApi = createApi({
  reducerPath: 'faqApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Faq'],
  endpoints: build => ({
    getFaqList: build.query<FaqListResponse, FaqListQuery>({
      query: params => ({
        url: '/faq/list',
        method: 'GET',
        params,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<FaqListResponse>(response),
      providesTags: ['Faq'],
    }),

    getFaqById: build.query<FaqItem, number>({
      query: id => ({
        url: `/faq/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapApiData<FaqItem>(response),
      providesTags: (_result, _error, id) => [{ type: 'Faq', id }],
    }),

    createFaq: build.mutation<FaqItem, CreateFaqInput>({
      query: body => ({
        url: '/faq',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => unwrapApiData<FaqItem>(response),
      invalidatesTags: ['Faq'],
    }),

    updateFaq: build.mutation<FaqItem, { id: number; body: UpdateFaqInput }>({
      query: ({ id, body }) => ({
        url: `/faq/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => unwrapApiData<FaqItem>(response),
      invalidatesTags: (_result, _error, { id }) => ['Faq', { type: 'Faq', id }],
    }),
  }),
});

export const {
  useGetFaqListQuery,
  useGetFaqByIdQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
} = faqApi;
